import { useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet } from "lucide-react";
import { inventoryCategoryApi, inventoryApi } from "../api";

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryPagination } from "../components/InventoryPagination";
import { InventoryStatusBadge } from "../components/InventoryStatusBadge";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { useAsyncData, useDebouncedValue, usePageState } from "../hooks";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "DISCONTINUED", label: "Discontinued" },
];

const ITEM_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "CONSUMABLE", label: "Consumable" },
  { value: "CAPITAL_ASSET", label: "Capital Asset" },
  { value: "SERVICE", label: "Service" },
  { value: "WORKS", label: "Works" },
];

const STOCK_OPTIONS: { value: "" | "LOW" | "GOOD" | "OUT"; label: string }[] = [
  { value: "", label: "All Stock Levels" },
  { value: "LOW", label: "Low Stock" },
  { value: "GOOD", label: "Good Stock" },
  { value: "OUT", label: "Out of Stock" },
];

export default function InventoryItemsPage() {
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [status, setStatus] = useState("");
  const [itemType, setItemType] = useState("");
  const [stockFilter, setStockFilter] = useState<"" | "LOW" | "GOOD" | "OUT">("");
  const [exporting, setExporting] = useState(false);
  const debouncedQ = useDebouncedValue(q);
  const { page, size, setPage, setSize } = usePageState(10);

  // Filter dropdown only needs categories still in use — inactive ones would just be dead ends.
  const categories = useAsyncData(() => inventoryCategoryApi.list({ active: true }), []);

  const { data, loading, error } = useAsyncData(
    () => inventoryApi.listItems({ q: debouncedQ, page, size, itemType, categoryId, status, stockFilter }),
    [debouncedQ, page, size, itemType, categoryId, status, stockFilter],
  );

  // Backend now filters on categoryId/status/itemType/stockFilter server-side, so `data.content`
  // should already match. This is just a safety net for stale API builds — kept in sync with the
  // backend's 3-way partition: OUT = stock <= 0, LOW = 0 < stock <= reorder level, GOOD = stock > reorder level.
  // (belowReorderLevel alone isn't enough to distinguish LOW from OUT since it's true for both.)
  const filtered = data?.content.filter((item) => {
    if (status && item.status !== status) return false;
    if (categoryId && item.categoryId !== categoryId) return false;
    if (itemType && item.itemType !== itemType) return false;
    const stockUnits = item.currentStockUnits || 0;
    if (stockFilter === "OUT" && stockUnits > 0) return false;
    if (stockFilter === "LOW" && !(item.belowReorderLevel && stockUnits > 0)) return false;
    if (stockFilter === "GOOD" && item.belowReorderLevel) return false;
    return true;
  }) ?? [];

  return (
    <div className="space-y-5">
      <InventoryPageHeader
        title="Inventory Items"
        actions={
          <div className="flex gap-2">
            <button
              onClick={async () => {
                setExporting(true);
                try {
                  downloadBlob(
                    await inventoryApi.exportItems({ q: debouncedQ, itemType, categoryId, status, stockFilter }),
                    `inventory-items-${new Date().toISOString().slice(0, 10)}.xlsx`,
                  );
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {exporting ? "Exporting…" : "Export Excel"}
            </button>
            <Link to="/inventory/items/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
              New Item
            </Link>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          className="input max-w-xs"
          placeholder="Search by code or name..."
          value={q}
          onChange={(e) => { setPage(0); setQ(e.target.value); }}
        />
        <select
          className="input w-48"
          value={categoryId}
          onChange={(e) => { setPage(0); setCategoryId(e.target.value ? Number(e.target.value) : ""); }}
        >
          <option value="">All Categories</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className="input w-40"
          value={status}
          onChange={(e) => { setPage(0); setStatus(e.target.value); }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          className="input w-40"
          value={itemType}
          onChange={(e) => { setPage(0); setItemType(e.target.value); }}
        >
          {ITEM_TYPE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select
          className="input w-44"
          value={stockFilter}
          onChange={(e) => { setPage(0); setStockFilter(e.target.value as typeof stockFilter); }}
        >
          {STOCK_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading && <InventoryLoading label="Loading inventory items..." />}
      {error && <InventoryError message={error} />}

      {!loading && !error && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Issue Stock</th>
                <th className="px-4 py-3">Stock Unit Balance</th>
                <th className="px-4 py-3">Reorder Level</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((item) => (
                <tr key={item.id} className={`hover:bg-slate-50 ${item.belowReorderLevel ? "border-l-2 border-l-amber-400" : ""}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{item.code}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{item.name}</div>
                    {item.belowReorderLevel && (
                      <div className="text-xs font-semibold text-amber-600">Low stock</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{item.categoryName || "-"}</td>
                  <td className={`px-4 py-3 font-medium ${item.belowReorderLevel ? "text-amber-700" : "text-slate-900"}`}>
                    {Number(item.currentStockUnits || 0).toLocaleString()} {item.issueUnitName}
                    {item.belowReorderLevel && (
                      <span className="ml-1.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">Low</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {Number(item.currentStockInStockUnits || 0).toLocaleString()} {item.stockUnitName}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {item.reorderLevelInIssueUnits != null
                      ? `${item.reorderLevelInIssueUnits} ${item.issueUnitName}`
                      : <span className="italic text-slate-400">Not set</span>}
                  </td>
                  <td className="px-4 py-3"><InventoryStatusBadge value={item.status} /></td>
                  <td className="px-4 py-3 text-slate-500">
                    {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <Link to={`/inventory/items/${item.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                      <Link to={`/inventory/items/${item.id}/edit`} className="font-semibold text-slate-600 hover:underline">Edit</Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No items found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <InventoryPagination page={page} setPage={setPage} size={size} setSize={setSize} data={data} />
    </div>
  );
}
